import { Search } from '.generated/Feature.CW.model';
import Pagination from 'helpers/Coveo/Pagination';
import QuerySummary from 'helpers/Coveo/QuerySummary';
import Sort from 'helpers/Coveo/SortDropdown';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import {
  CoveoEngineProvider,
  useCoveoEngine,
} from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { JSX, useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import { buildInteractiveResult, InteractiveResult, Result } from '@coveo/headless';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import AppliedFacetFilters from 'helpers/Coveo/AppliedFacetFilters';
import UrlManagerController from 'helpers/Coveo/UrlManagerController';
import { ButtonGroupProps } from 'helpers/SitecoreWrappers/ButtonGroup/ButtonGroup';
import InitialsAndFacets from 'helpers/Coveo/InitialsAndFacets';
import { SearchBar } from './GlobalSearch';
import type { LocationInputHandle } from './GlobalSearch';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import classNames from 'classnames';
import { SvgIcon } from 'helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import NoResultsMessage from 'helpers/Coveo/NoResultsMessage';
import { useSiteSettings } from 'lib/utils/page-data-static-props/pageDataContext';
import AZResultCard from './AZResults';
import { useCoveoSearchSubscription } from 'lib/hooks/useCoveoSearchSubscription';

export type AZTeachingSheetsProps = Search.AZSearchTeachingSheets &
  ButtonGroupProps & {
    params: {
      Theme: string;
    };
    templateId: string;
  };

type TeachingSheetRaw = {
  cwformat?: string[];
  cwtitle?: string;
  cwdescription?: string;
};

const AZTeachingSheets = (props: AZTeachingSheetsProps): JSX.Element => {
  const contextWithSettings = useSiteSettings()?.fields;

  const [coveoConfig, setCoveoConfig] = useState({
    searchHub: '',
    pipeline: '',
    accessToken: '',
    organizationId: '',
  });

  useEffect(() => {
    setCoveoConfig({
      searchHub: contextWithSettings?.aZSearchHubTeachingSheets?.value || '',
      pipeline: contextWithSettings?.aZPipelineTeachingSheets?.value || '',
      accessToken: contextWithSettings?.aZAccessTokenTeachingSheets?.value || '',
      organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
    });
  }, [
    contextWithSettings?.aZSearchHubTeachingSheets?.value,
    contextWithSettings?.aZPipelineTeachingSheets?.value,
    contextWithSettings?.aZAccessTokenTeachingSheets?.value,
  ]);

  if (!coveoConfig?.organizationId && !coveoConfig?.accessToken && !coveoConfig?.pipeline) {
    return <></>;
  }

  return (
    <>
      <CoveoEngineProvider config={coveoConfig}>
        <AZTeachingSheetsResultList {...props} />
        <UrlManagerController />
      </CoveoEngineProvider>
    </>
  );
};

export default AZTeachingSheets;

export const AZTeachingSheetsResultList = (props: AZTeachingSheetsProps): JSX.Element => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { getDictionaryValue } = useDictionary();
  const engine = useCoveoEngine(); //Custom hook for engine , engine connects NextApp to Coveo Search
  const { ContextResults, firstSearchExecuted } = useCoveoSearchSubscription();

  const fieldsToInclude = [
    'cwdescription',
    'cwinitial',
    'cwpagetype',
    'cwtitle',
    'clickUri',
    'cwformat',
    'cwdraftnumber',
  ];
  // Inside component
  const searchBarRef = useRef<LocationInputHandle>(null);

  const TAILWIND_VARIANTS = tv({
    slots: {
      base: ['bg-blue-50 py-[64px]'],
      headingStyle: ['leading-58 font-bold text-gray-900 font-centuryGothic text-48 lg:mb-0 mb-8'],
      titleLinkWrapperStyle: ['lg:flex justify-between items-center'],
      containerStyle: ['max-w-screen-xxl mx-auto lg:px-[80px] px-10'],
      subHeadingStyle: [
        'leading-[22px] font-bold text-gray-900 font-centuryGothic text-16 my-[15px]',
      ],
    },
  });

  // stop scroll on drawer open in mobile
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  const resultsToRender = ContextResults?.results || [];

  return (
    <>
      <ComponentWrapper className={TAILWIND_VARIANTS().base()}>
        <div className={TAILWIND_VARIANTS().containerStyle()}>
          <div className={TAILWIND_VARIANTS().titleLinkWrapperStyle()}>
            <h1 className={TAILWIND_VARIANTS().headingStyle()}>
              <PlainTextWrapper field={props?.fields?.pageTitle} />
            </h1>
          </div>
          <div>
            <SearchBar
              ref={searchBarRef}
              engine={engine}
              placeholder={props?.fields?.searchTermPlaceholderText}
              className="my-[40px]"
              autoSubmitOnSetValue={true}
            />
          </div>{' '}
        </div>
      </ComponentWrapper>
      <ComponentWrapper className="bg-white">
        <div className="max-w-screen-xxl mx-auto lg:px-[80px] px-10 coveo-search-list">
          <div className="flex lg:flex-row flex-col gap-16">
            <div className="md:max-w-[248px] w-full md:mr-16 mr-0 lg:block hidden">
              <InitialsAndFacets
                initialFacetField="cwinitial"
                initialFacetLabel="First Letter"
                facetGroupProps={{
                  fields: ['cwspecialties', 'cwcontenttype', 'cwpopulation', 'cwformat'],
                  labels: {
                    cwspecialties: 'Specialty',
                    cwcontenttype: 'Content Type',
                    cwpopulation: 'Population',
                    cwformat: 'Format',
                  },
                  numberOfValues: 10,
                  sortCriteria: 'occurrences',
                }}
              />
            </div>
            {isDrawerOpen && (
              <div
                className={classNames(
                  'fixed inset-0 z-50 bg-white transform transition-transform duration-500 ease-in-out flex flex-col lg:hidden',
                  {
                    'translate-x-0': isDrawerOpen,
                    'translate-x-full': !isDrawerOpen,
                  }
                )}
              >
                <div className="flex justify-between items-center px-10 py-[25px] border-b border-gray-200">
                  <div className="text-20 font-bold leading-[28px] font-centuryGothic">
                    {getDictionaryValue('SetFilters')}
                  </div>
                  <button onClick={() => setIsDrawerOpen(false)} aria-label="Close">
                    <SvgIcon icon="close" size="lg" className="w-10 h-10 [&>path]:fill-blue-base" />
                  </button>
                </div>

                <div className="mx-10 py-[25px] flex items-center justify-between border-b-2 border-gray-50">
                  <div className="text-[30px] leading-[36px] font-bold text-black font-centuryGothic">
                    {getDictionaryValue('Filters') || 'Filters'}
                  </div>
                </div>
                <div className="flex-grow overflow-y-auto px-10">
                  <InitialsAndFacets
                    initialFacetField="cwinitial"
                    initialFacetLabel="First Letter"
                    facetGroupProps={{
                      fields: ['cwspecialties', 'cwcontenttype', 'cwpopulation', 'cwformat'],
                      labels: {
                        cwspecialties: 'Specialty',
                        cwcontenttype: 'Content Type',
                        cwpopulation: 'Population',
                        cwformat: 'Format',
                      },
                      numberOfValues: 10,
                      sortCriteria: 'occurrences',
                    }}
                  />
                </div>
                <div className="p-10 border-t border-gray-200">
                  <ButtonWrapper
                    ctaVariant="primary"
                    onClick={() => setIsDrawerOpen(false)}
                    className="w-full bg-blue-base text-white py-4 rounded-md text-18 font-semibold"
                  >
                    {getDictionaryValue('ViewResults')}
                  </ButtonWrapper>
                </div>
              </div>
            )}
            <div className="lg:hidden mb-4">
              <ButtonWrapper
                ctaVariant="secondary"
                text="Set Filters"
                ctaIcon="funnel"
                onClick={() => setIsDrawerOpen(true)}
                className="w-full [&>svg]:mt-[-5px] gap-x-4"
                ctaIconAlignment="left"
              />
            </div>

            <div className="md:w-[calc(100%-248px)]">
              <div className="py-6">
                <AppliedFacetFilters
                  fields={['cwinitial', 'cwspecialties']}
                  labels={{
                    cwinitial: 'First letter',
                    cwspecialties: 'Type',
                  }}
                />
              </div>
              <div className="flex justify-start items-center py-6 w-full flex-wrap">
                {/* Total Count of results */}
                <QuerySummary />
                {/* Sort control lets you select criteria to apply to search results : hidden as per design */}
                <div className="hidden">
                  <Sort fieldsToInclude={fieldsToInclude} enableNewestSort={true} />
                </div>
              </div>
              <div className="resultlist-wrapper">
                {firstSearchExecuted ? (
                  resultsToRender?.length > 0 ? (
                    <div className="">
                      <div>
                        <section>
                          {resultsToRender.map((item: Result, index) => {
                            const raw = item.raw as TeachingSheetRaw;
                            const controller: InteractiveResult | null = engine
                              ? buildInteractiveResult(engine, {
                                  options: { result: item },
                                })
                              : null;

                            return (
                              <AZResultCard
                                key={index}
                                card={{
                                  clickUri: item?.clickUri,
                                  cwtitle: raw?.cwtitle || '',
                                  cwdescription: raw?.cwdescription || '',
                                  cwformat: raw?.cwformat || [],
                                  controller: controller ?? undefined,
                                }}
                              />
                            );
                          })}
                        </section>
                      </div>
                    </div>
                  ) : (
                    <>
                      <NoResultsMessage />
                    </>
                  )
                ) : null}
              </div>

              <div className="pagination-wrapper">
                <Pagination fieldsToInclude={fieldsToInclude} className="justify-end mt-12" />
              </div>
            </div>
          </div>
        </div>
      </ComponentWrapper>
    </>
  );
};
